import type { SlotOccupancy, SlotOccupancyPeriod } from '@/api/core/inventory';
import { isApiError } from '@/api/http/errors';
import { useOperatorSession } from '@/auth/runtime';
import { InlineLoading } from '@/components/inline-loading';
import { formatDateTimeWithOffset } from '@/lib/format';
import { Button } from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  hasInventoryReadAccess,
  slotOccupancyQuery,
  slotOccupancyRuntime,
  type SlotOccupancyRuntime,
} from './slot-occupancy-queries';

function localInputToIso(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}

function statusLabel(value: string) {
  const labels: Record<string, string> = {
    CURRENT: '현재 확인됨',
    UNKNOWN: '미확인',
    SELECTED: '선택됨',
    NOT_SELECTED: '선택 안 됨',
    STORED_ACTIVE_UNVERIFIED: '저장 상태 활성 · 실제 적용 미확인',
    STORED_NOT_ACTIVE: '저장 상태 비활성',
    ACTIVE: '활성',
    SUSPENDED: '중지',
    DRAINING: '반환 진행',
    RETURNED: '반환됨',
    DISCOVERED: '발견됨',
    ELIGIBILITY_CHECKED: '적격성 확인됨',
    FENCE_PREPARED: '외부 변경 차단 준비됨',
    BARRIER_CHECKED: '차단 확인됨',
    RETURN_READY: '반환 준비됨',
    HELD: '임시 점유',
    CONFIRMED: '확정 점유',
    RELEASED: '해제됨',
    EXPIRED: '만료됨',
    BOOKING: '예약',
    ORDER: '주문',
    REDACTED: '연결 유형 비공개',
    PAYMENT: '결제',
    MOVE: '이동',
    BLOCKED: '차단됨',
    OBSERVED_NO_BLOCKS: '차단 없음 확인',
    CREDENTIAL_RECOVERY_PENDING: '출입 자격 회수 대기',
    ACCESS_GUARD_UNAVAILABLE: '출입 보호 상태 미조회',
    ACCESS_GUARD_CHANGED: '확인 중 출입 보호 정보가 바뀜',
    APPROVAL_NOT_REQUESTED: '승인 요청 없음',
    APPROVAL_REJECTED: '승인 거절',
    PRODUCT_COMPLETED: '상품 처리 완료',
    COMPENSATION_COMPLETED: '되돌림 처리 완료',
    DESTINATION_CONFIRMED: '도착지 확인',
    MOVE_CANCELLATION_COMPENSATED: '이동 취소 되돌림 완료',
    OBSERVED: '확인 완료',
  };
  return labels[value] ?? value;
}
function errorLabel(error: unknown) {
  if (!isApiError(error)) return '기간별 점유를 조회하지 못했습니다.';
  if (error.kind === 'invalid-response')
    return '기간별 객실 점유 정보를 불러오지 못했습니다. 관리자에게 문의해 주세요.';
  if (error.status === 401) return '로그인 세션을 확인해 주세요.';
  if (error.status === 403) return '객실 점유 조회 권한이 없습니다.';
  if (error.status === 404) return '이 객실 슬롯을 찾을 수 없습니다.';
  if (error.status === 503)
    return '지금은 기간별 점유를 조회할 수 없습니다. 잠시 후 다시 조회해 주세요.';
  return '기간별 점유를 조회하지 못했습니다.';
}

export function SlotOccupancySection({
  propertyId,
  roomId,
  roomNo,
  slotId,
  slotNo,
  generation,
  runtime = slotOccupancyRuntime,
  isCurrent,
}: {
  propertyId: string;
  roomId: string;
  roomNo: number;
  slotId: string;
  slotNo: string;
  generation: number;
  runtime?: SlotOccupancyRuntime;
  isCurrent: () => boolean;
}) {
  const session = useOperatorSession(runtime.session);
  const [draftStartsAt, setDraftStartsAt] = useState('');
  const [draftEndsAt, setDraftEndsAt] = useState('');
  const [formError, setFormError] = useState('');
  const [appliedPeriod, setAppliedPeriod] = useState<SlotOccupancyPeriod | null>(null);
  const allowed = hasInventoryReadAccess(runtime.session, generation);
  const query = useQuery(
    slotOccupancyQuery(
      runtime,
      generation,
      propertyId,
      roomId,
      slotId,
      appliedPeriod ?? { startsAt: '', endsAt: '' },
      appliedPeriod !== null && allowed,
      isCurrent,
    ),
  );
  const apply = () => {
    const startsAt = localInputToIso(draftStartsAt);
    const endsAt = localInputToIso(draftEndsAt);
    if (!draftStartsAt || !draftEndsAt || !startsAt || !endsAt) {
      setFormError('조회 기간의 시작과 종료를 모두 입력해 주세요.');
      return;
    }
    const duration = Date.parse(endsAt) - Date.parse(startsAt);
    if (duration <= 0) {
      setFormError('종료 시각은 시작 시각보다 늦어야 합니다.');
      return;
    }
    if (duration > 366 * 24 * 60 * 60 * 1000) {
      setFormError('조회 기간은 최대 366일입니다.');
      return;
    }
    setFormError('');
    if (appliedPeriod?.startsAt === startsAt && appliedPeriod.endsAt === endsAt) {
      if (!query.isFetching) void query.refetch();
    } else {
      setAppliedPeriod({ startsAt, endsAt });
    }
  };
  const denied = session.deniedDomains.includes('inventory') || !allowed;
  const data = query.isError || query.isFetching ? undefined : query.data;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '현재 기기 시간대';

  return (
    <section
      className="mt-4 flex min-w-0 flex-col gap-3 border-t pt-4"
      aria-label="기간별 점유 조회"
    >
      <div>
        <h4 className="heading-sm">슬롯 {slotNo} · 기간별 점유</h4>
        <p className="body-sm text-muted-foreground">
          {roomNo}호에서 선택한 슬롯 하나의 저장 점유를 조회합니다.
        </p>
      </div>
      <form
        aria-label="기간별 점유 조회 조건"
        className="grid w-full grid-cols-1 items-end gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]"
        onSubmit={(event) => {
          event.preventDefault();
          apply();
        }}
      >
        <label className="flex w-full min-w-0 flex-col gap-1">
          <span className="label">조회 기간 시작</span>
          <input
            type="datetime-local"
            aria-label="점유 조회 기간 시작"
            className="border-input bg-background body-sm h-9 w-full min-w-0 rounded-md border px-3"
            value={draftStartsAt}
            onChange={(event) => {
              setDraftStartsAt(event.target.value);
            }}
          />
        </label>
        <label className="flex w-full min-w-0 flex-col gap-1">
          <span className="label">조회 기간 종료</span>
          <input
            type="datetime-local"
            aria-label="점유 조회 기간 종료"
            className="border-input bg-background body-sm h-9 w-full min-w-0 rounded-md border px-3"
            value={draftEndsAt}
            onChange={(event) => {
              setDraftEndsAt(event.target.value);
            }}
          />
        </label>
        <Button type="submit" className="w-full sm:w-auto" disabled={denied}>
          기간별 점유 조회
        </Button>
      </form>
      <p className="body-sm text-muted-foreground">
        입력 시각은 {timezone} 기준입니다. 시작과 종료를 입력한 뒤 조회를 눌러 주세요.
      </p>
      {formError && (
        <p role="alert" className="body-sm text-destructive">
          {formError}
        </p>
      )}
      {denied ? (
        <p role="status" className="body-sm text-warning-foreground">
          객실 점유 조회 권한이 없습니다. 지점과 객실 조회 권한은 별도로 유지됩니다.
        </p>
      ) : appliedPeriod === null ? (
        <p role="status" className="body-sm text-muted-foreground">
          기간을 입력한 뒤 조회하면 저장된 점유를 확인할 수 있습니다.
        </p>
      ) : query.isPending || query.isFetching ? (
        <InlineLoading>조회한 기간의 저장 점유를 불러오고 있습니다.</InlineLoading>
      ) : query.isError ? (
        <div role="alert" className="flex flex-col items-start gap-2">
          <p className="body-sm text-warning-foreground">{errorLabel(query.error)}</p>
          <Button variant="outline" onClick={() => void query.refetch()}>
            기간별 점유 다시 조회
          </Button>
        </div>
      ) : data ? (
        <OccupancyResult result={data} />
      ) : null}
    </section>
  );
}

function OccupancyResult({ result }: { result: SlotOccupancy }) {
  return (
    <div className="flex min-w-0 flex-col gap-3" aria-label="기간별 점유 조회 결과">
      <p className="body-sm">
        조회 기간 {formatDateTimeWithOffset(result.startsAt)} –{' '}
        {formatDateTimeWithOffset(result.endsAt)}
        {' · '}확인 시각 {formatDateTimeWithOffset(result.observedAt)}
      </p>
      <p className="body-sm">
        저장 점유 {result.claims.length}건 · 충돌{' '}
        {result.claimConflictObserved ? '확인됨' : '확인되지 않음'}
        {' · '}지점·객실 연결 {statusLabel(result.topologyStatus)} · 관리권{' '}
        {statusLabel(result.authorityStatus)}
        {' · '}공용 출입 {statusLabel(result.accessObservationStatus)}
      </p>
      <dl className="grid min-w-0 gap-2 sm:grid-cols-2">
        <Fact label="지점·객실 연결" value={statusLabel(result.topologyStatus)} />
        <Fact label="객실 관리 대상" value={statusLabel(result.selectionStatus)} />
        <Fact label="관리권 확인" value={statusLabel(result.authorityStatus)} />
        <Fact
          label="점유 충돌 확인"
          value={result.claimConflictObserved ? '충돌 확인됨' : '충돌 확인되지 않음'}
        />
        <Fact label="출입 차단 확인" value={statusLabel(result.accessObservationStatus)} />
        <Fact label="회수 작업 진행 상태" value="미조회" />
        <Fact label="판매 판단" value="제공되지 않음" />
      </dl>
      {result.propertyId === null || result.roomId === null ? (
        <p className="body-sm text-warning-foreground">
          지점 또는 객실 연결은 현재 확인되지 않았습니다. 비어 있는 값을 연결 성공으로 보지
          않습니다.
        </p>
      ) : null}
      <details>
        <summary className="cursor-pointer">확인 사유와 ID 정보</summary>
        <dl className="mt-2 grid min-w-0 gap-2 sm:grid-cols-2">
          <Fact label="지점 ID" value={result.propertyId ?? '미확인'} />
          <Fact label="객실 ID" value={result.roomId ?? '미확인'} />
          <Fact label="객실 슬롯 ID" value={result.slotId} />
          <Fact label="지점·객실 연결 확인 사유" value={result.topologyReason ?? '없음'} />
          <Fact
            label="관리 대상 선택 버전"
            value={result.selectionVersion === null ? '미기록' : String(result.selectionVersion)}
          />
          <Fact label="관리권 확인 사유" value={result.authorityReason ?? '없음'} />
          <Fact label="공용 출입 확인 사유" value={result.accessObservationReason ?? '없음'} />
        </dl>
      </details>
      {result.claims.length === 0 ? (
        <p className="body-sm text-muted-foreground">
          이 기간에 겹치는 저장 점유가 없습니다. 점유가 없다고 해서 판매나 출입이 가능하다는 뜻은
          아닙니다.
        </p>
      ) : (
        <details>
          <summary className="cursor-pointer">저장 점유 {result.claims.length}건</summary>
          <ul className="mt-2 flex min-w-0 flex-col gap-3">
            {result.claims.map((claim) => (
              <li key={claim.claimId} className="border-border min-w-0 rounded border p-3">
                <dl className="grid min-w-0 gap-2 sm:grid-cols-2">
                  <Fact label="점유 ID" value={claim.claimId} />
                  <Fact label="저장 상태" value={statusLabel(claim.status)} />
                  <Fact
                    label="저장 기간"
                    value={`${formatDateTimeWithOffset(claim.startAt)} – ${formatDateTimeWithOffset(claim.endAt)}`}
                  />
                  <Fact
                    label="임시 점유 만료 시각"
                    value={formatDateTimeWithOffset(claim.expiresAt)}
                  />
                  <Fact
                    label="확인 당시 임시 점유 만료"
                    value={claim.expiredAtObservation ? '만료됨' : '만료되지 않음'}
                  />
                  <Fact label="소유 유형" value={statusLabel(claim.ownerType)} />
                  <Fact label="소유자 ID" value={claim.ownerId ?? '현재 연결 확인 안 됨'} />
                  <Fact
                    label="예약 ID"
                    value={claim.bookingId ?? '현재 같은 슬롯 연결 확인 안 됨'}
                  />
                  <Fact label="확정 증거 ID" value={claim.confirmationId ?? '미기록'} />
                  <Fact
                    label="관리권 차수 / 점유 회차"
                    value={`${String(claim.ownershipEpoch)} / ${String(claim.generation)}`}
                  />
                  <Fact
                    label="결제·이동 보호 이력"
                    value={claim.pins.length ? `${String(claim.pins.length)}건` : '없음'}
                  />
                </dl>
                {claim.pins.length > 0 && (
                  <ul className="mt-2 flex min-w-0 flex-col gap-2">
                    {claim.pins.map((pin) => (
                      <li key={`${pin.pinType}-${pin.referenceId}`} className="break-all">
                        {statusLabel(pin.pinType)} 보호 이력 · {statusLabel(pin.status)} · 연결 ID{' '}
                        {pin.referenceId}
                        {' · '}해제 근거{' '}
                        {pin.releaseEvidence ? statusLabel(pin.releaseEvidence) : '없음'}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
      <details>
        <summary className="cursor-pointer">저장 관리권 이력 {result.ownerships.length}건</summary>
        {result.ownerships.length ? (
          <ul className="mt-2 flex min-w-0 flex-col gap-2">
            {result.ownerships.map((ownership) => (
              <li key={ownership.ownershipId} className="break-all">
                {statusLabel(ownership.status)} · 관리권 ID {ownership.ownershipId} · 차수{' '}
                {String(ownership.epoch)}
                {' · '}유효 기록 {formatDateTimeWithOffset(ownership.effectiveFrom)} –{' '}
                {ownership.effectiveUntil
                  ? formatDateTimeWithOffset(ownership.effectiveUntil)
                  : '종료 시각 미기록'}
                {' · '}지점 대응 버전 {String(ownership.mappingRevision)}
              </li>
            ))}
          </ul>
        ) : (
          <p className="body-sm text-muted-foreground mt-2">표시할 저장 관리권 이력이 없습니다.</p>
        )}
      </details>
      <details>
        <summary className="cursor-pointer">
          공용 출입 그룹 {result.accessGroups.length}개 · 차단 및 회수 작업 연결
        </summary>
        {result.accessGroups.length ? (
          <ul className="mt-2 flex min-w-0 flex-col gap-2">
            {result.accessGroups.map((group) => (
              <li key={group.groupId} className="border-border min-w-0 rounded border p-3">
                <p className="break-all">
                  출입 그룹 {group.groupId} · {statusLabel(group.status)} · 출입 보호 회차{' '}
                  {group.guardGeneration === null ? '미조회' : String(group.guardGeneration)}
                  {group.reason ? ` · ${statusLabel(group.reason)}` : ''}
                </p>
                {group.blocks.length ? (
                  <ul className="mt-2 flex min-w-0 flex-col gap-1">
                    {group.blocks.map((block) => (
                      <li key={block.blockId} className="break-all">
                        차단 {block.blockId} · 원인 점유 {block.claimId} · 점유 회차{' '}
                        {String(block.claimGeneration)}
                        {' · '}연결된 회수 작업 {block.recoveryOperationId} (실제 진행 상태 미조회)
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="body-sm text-muted-foreground mt-1">
                    {group.status === 'UNKNOWN'
                      ? '차단 여부를 모두 확인하지 못했습니다.'
                      : group.status === 'OBSERVED_NO_BLOCKS'
                        ? '현재 출입 보호 기준으로 차단이 없다고 확인했습니다. 판매 승인이나 실제 출입 성공을 뜻하지는 않습니다.'
                        : '표시할 차단 상세가 없습니다.'}
                  </p>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="body-sm text-muted-foreground mt-2">
            출입 그룹 자료가 없습니다. 이 결과만으로는 출입 차단이 없다고 단정할 수 없습니다.
          </p>
        )}
      </details>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="body-sm text-muted-foreground">{label}</dt>
      <dd className="break-all">{value}</dd>
    </div>
  );
}
